// Setting → Finance Document — เอกสาร Finance ของทุกแบรนด์รวมในตารางเดียว
//
// เมนู Finance ของแต่ละแบรนด์ตอบได้แค่ "แบรนด์นี้มีไฟล์อะไร" คำถามที่ตอบไม่ได้คือ
// "งาน P2026-200 ส่ง Media Plan แล้วหรือยัง" เพราะต้องเปิดทีละแบรนด์ไล่ดู
// หน้านี้รวมทุกแบรนด์ไว้ที่เดียวและเรียงตามรหัสงานได้ ซึ่งเป็นมุมที่ใช้ตามงานจริง
//
// อ่านอย่างเดียว — อัปโหลด แก้หมวด และลบ ยังทำที่เมนู Finance ของแบรนด์นั้นเหมือนเดิม
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { documentFileUrl } from "@features/documents/api/documents-api";
import { categoryLabel } from "@features/finance/lib/categories";
import { apiErrorMessage } from "@shared/api/client";
import { fmtInt } from "@shared/lib/format";
import { IconDownload, IconSearch } from "@shared/ui/icons";
import { cn } from "@shared/lib/utils";
import { fetchFinanceDocs, financeDocKeys, type FinanceDoc } from "../api/settings-api";

/**
 * รหัสงานจากชื่อแคมเปญ — แคมเปญที่สร้างจากทะเบียน Project Code ขึ้นต้นด้วย [P2026-200]
 * แคมเปญที่ตั้งชื่อเองไม่มีรหัส ซึ่งเป็นคนละเรื่องกับ "ยังไม่ได้ผูกแคมเปญ"
 */
function codeOf(doc: FinanceDoc): string {
  return /^\[(P\d{4}-\d+)\]/i.exec((doc.campaignName ?? "").trim())?.[1]?.toUpperCase() ?? "";
}

/**
 * เรียงตามรหัสงาน
 *
 * เทียบปีกับเลขงานเป็นตัวเลข ไม่ใช่ตัวอักษร — ไม่งั้น P2026-9 จะมาหลัง P2026-20
 * ไฟล์ที่ไม่มีรหัสไปอยู่ท้ายเสมอไม่ว่าจะเรียงทางไหน เพราะไม่มีตำแหน่งในลำดับนั้น
 */
function byCode(a: FinanceDoc, b: FinanceDoc, dir: 1 | -1): number {
  const ca = codeOf(a);
  const cb = codeOf(b);
  if (!ca && !cb) return 0;
  if (!ca) return 1;
  if (!cb) return -1;
  const [ya, na] = ca.slice(1).split("-").map(Number);
  const [yb, nb] = cb.slice(1).split("-").map(Number);
  if (ya !== yb) return (ya - yb) * dir;
  return (na - nb) * dir;
}

export function FinanceDocPage() {
  const [dir, setDir] = useState<1 | -1>(-1);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [brand, setBrand] = useState("");

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: financeDocKeys.all,
    queryFn: fetchFinanceDocs,
    retry: false,
  });

  const all = data ?? [];
  const categories = [...new Set(all.map((d) => d.category).filter(Boolean))] as string[];
  /**
   * เฉพาะกลุ่มที่มีไฟล์จริง พร้อมจำนวนแคมเปญของกลุ่มนั้น
   *
   * นับ "แคมเปญ" ไม่ใช่ "ไฟล์" — แคมเปญเดียวมีได้หลายไฟล์ (Master Plan + Media Plan)
   * ตัวเลขที่บอกจำนวนไฟล์จึงไม่ได้บอกว่ามีงานกี่งานในกลุ่มนั้น
   * ไฟล์ที่ยังไม่ผูกแคมเปญไม่ถูกนับ เพราะยังไม่รู้ว่าเป็นของงานไหน
   */
  const brands = [
    ...all
      .reduce((acc, d) => {
        if (!d.brandName) return acc;
        const campaigns = acc.get(d.brandName) ?? new Set<string>();
        if (d.campaignId) campaigns.add(d.campaignId);
        return acc.set(d.brandName, campaigns);
      }, new Map<string, Set<string>>())
      .entries(),
  ]
    .map(([name, campaigns]) => ({ name, campaigns: campaigns.size }))
    .sort((a, b) => a.name.localeCompare(b.name, "th"));
  const needle = q.trim().toLowerCase();

  const shown = all
    .filter(
      (d) =>
        (!brand || d.brandName === brand) &&
        (!category || d.category === category) &&
        (!needle ||
          `${d.brandName ?? ""} ${codeOf(d)} ${d.name} ${d.campaignName ?? ""}`
            .toLowerCase()
            .includes(needle)),
    )
    .sort((a, b) => byCode(a, b, dir));

  return (
    <>
      <div className="page-head">
        <div>
          <div className="breadcrumb">Setting / Finance Document</div>
          <h1>Finance Document</h1>
          <p className="subtitle">
            เอกสารในเมนู Finance ของทุก Product Group · อัปโหลดและลบยังทำที่หน้าแบรนด์เหมือนเดิม
          </p>
        </div>
      </div>

      {isPending ? (
        <div className="panel">
          <p className="muted">กำลังโหลดเอกสาร…</p>
        </div>
      ) : isError ? (
        <div className="panel empty-state">
          <h3>โหลดเอกสารไม่สำเร็จ</h3>
          <p className="muted">{apiErrorMessage(error)}</p>
          <button type="button" className="btn-primary inline-btn" onClick={() => void refetch()}>
            ลองอีกครั้ง
          </button>
        </div>
      ) : (
        <div className="panel">
          <div className="pc-filters">
            <div className="search-box grow-box">
              <IconSearch />
              <input
                type="text"
                placeholder="ค้นหาแบรนด์ รหัสงาน หรือชื่อไฟล์…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <label className="camp-select-wrap">
              <span className="muted camp-select-lbl">กลุ่ม</span>
              <select
                className="camp-select"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
              >
                <option value="">ทุกกลุ่ม</option>
                {brands.map((b) => (
                  <option key={b.name} value={b.name}>
                    {b.name} ({b.campaigns})
                  </option>
                ))}
              </select>
            </label>
            <label className="camp-select-wrap">
              <span className="muted camp-select-lbl">หมวด</span>
              <select
                className="camp-select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">ทุกหมวด</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {categoryLabel(c)}
                  </option>
                ))}
              </select>
            </label>
            <span className="muted small-note pc-count">
              {shown.length}/{all.length} ไฟล์
            </span>
          </div>

          <div className="pc-wrap">
            <table className="pc-table">
              <thead>
                <tr>
                  <th>Product Group</th>
                  <th>
                    {/* เรียงตามรหัสงานคือมุมหลักของหน้านี้ จึงเป็นคอลัมน์เดียวที่กดเรียงได้ */}
                    <button
                      type="button"
                      className="th-sort"
                      onClick={() => setDir((d) => (d === 1 ? -1 : 1))}
                      title="เรียงตามรหัสแคมเปญ"
                    >
                      รหัสแคมเปญ <span aria-hidden="true">{dir === 1 ? "↑" : "↓"}</span>
                    </button>
                  </th>
                  <th>ชื่อไฟล์</th>
                  <th>หมวดเอกสาร</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((d) => {
                  const code = codeOf(d);
                  return (
                    <tr key={d.id}>
                      <td>{d.brandName ?? <span className="muted">— ไม่ทราบกลุ่ม —</span>}</td>
                      <td>
                        {code ? (
                          <span className="code-chip">{code}</span>
                        ) : (
                          <span className="muted" title={d.campaignName ?? "ยังไม่ได้ผูกแคมเปญ"}>
                            {d.campaignName ? "ไม่มีรหัส" : "ยังไม่ผูกแคมเปญ"}
                          </span>
                        )}
                      </td>
                      <td className="pc-name">
                        <a
                          className="fd-file"
                          href={documentFileUrl(d.id, { download: true })}
                          download={d.name}
                          title={`ดาวน์โหลด ${d.name}`}
                        >
                          <span className="grow">{d.name}</span>
                          <IconDownload size={16} />
                        </a>
                        <span className="pf-meta muted">
                          {fmtInt(Math.round(d.size / 1024))} KB
                        </span>
                      </td>
                      <td className={cn(!d.category && "muted")}>{categoryLabel(d.category)}</td>
                    </tr>
                  );
                })}
                {!shown.length && (
                  <tr>
                    <td colSpan={4} className="pc-empty">
                      <span className="muted">
                        {all.length ? "ไม่พบไฟล์ที่ตรงกับที่กรอง" : "ยังไม่มีเอกสารในเมนู Finance"}
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
